import * as HighStock from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import * as React from "react";
import { StringHelper } from "../../../../../../../../react-core/Helpers/StringHelper";
import { FormComponent } from "../../../../../../../../react-core/Html/FormComponent";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { SubAccountType } from "../../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { PerformanceGraphDataItemForDisplay } from "../../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Performance/PerformanceGraphDataItemForDisplay";
import { ProductGraphData } from "../../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Performance/ProductGraphData";
import { subAccountTypeMap } from "../Resource/Contents";
import { LoadingSkeletonType } from "../../../../../../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeleton } from "../../../../../../../Shared/LoadingSkeleton/LoadingSkeleton";
import { PresentationContext } from "../../Common/PresentationViewWrapper";
import CoachMarker from "../../../../../../../Shared/CoachMarkers/CoachMarker";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import { getNoDataMsg } from "../../Transactions/Resource/Contents";

interface IPerformanceChartProps {
    data: ProductGraphData[];
    selectedProducts: string[];
    onClickGraghSeriesBtn: (clAccountId: string) => void;
}

interface IPerformanceChartState {}

export class PerformanceChart extends FormComponent<IPerformanceChartProps, IPerformanceChartState> {
    constructor(props: IPerformanceChartProps) {
        super(props);
    }

    private settings = {};

    private getOptions(series: any[]): Highcharts.Options {
        return {
            series: series,
            navigator: {
                enabled: true,
                height: 58,
                maskFill: "transparent",

                xAxis: {
                    type: "datetime",
                    labels: {
                        style: {
                            color: "var(--cta1)",
                        },
                        enabled: true,
                        formatter: function () {
                            const date = new Date(this.value);
                            const month = JsonDateParser.getMonthNameMapping((date.getMonth() + 1).toString(), true);
                            const year = JsonDateParser.getFormatDate(JsonDateParser.toJsonDateNoOffset(date), "yy");

                            const formatDate = month + "'" + year;
                            return formatDate;
                        },
                    },
                },
                series: {
                    type: "areaspline",
                    color: "#acbcc5",
                },
                handles: {
                    symbols: ["doublearrow", "doublearrow"],
                    lineWidth: 1,
                    width: 26,
                    height: 26,
                    backgroundColor: "white",
                    borderColor: "#4B4B4B",
                },
            },
            rangeSelector: {
                selected: 1.5,
            },
            scrollbar: {
                enabled: true,
                barBackgroundColor: "#d8d8d8",
                barBorderRadius: 0,
                barBorderWidth: 0,
                barBorderColor: "#979797",
                buttonBackgroundColor: "#d8d8d8",
                buttonBorderColor: "#a1a7ac",
                buttonBorderWidth: 1,
                buttonArrowColor: "#666666",
                buttonBorderRadius: 0,
                rifleColor: "#666666",
                trackBackgroundColor: "#ffffff",
                trackBorderWidth: 1,
                trackBorderColor: "#a1a7ac",
                trackBorderRadius: 0,
                height: 20,
            },
            title: {
                text: "",
            },
            legend: {
                enabled: false,
            },
            credits: {
                enabled: false,
            },
            chart: {
                type: "line",
                zoomType: "xy",
                backgroundColor: "transparent",
                height: 373,
            },
            plotOptions: {
                line: {
                    marker: {
                        enabled: false,
                    },
                },
            },
            xAxis: {
                tickWidth: 0,
                type: "datetime",
                labels: {
                    style: {
                        color: "var(--cta1)",
                    },
                    align: "center",
                    formatter: function () {
                        const date = new Date(this.value);
                        const month = JsonDateParser.getMonthNameMapping((date.getMonth() + 1).toString(), true);
                        const year = JsonDateParser.getFormatDate(JsonDateParser.toJsonDateNoOffset(date), "yy");

                        const formatDate = month + "'" + year;
                        return formatDate;
                    },
                },
                title: {
                    text: "",
                },
                lineColor: "transparent",
            },
            yAxis: {
                labels: {
                    style: {
                        color: "var(--cta1)",
                    },
                    formatter: function () {
                        return "£" + HighStock.numberFormat(this.value, 0, ".", ",");
                    },
                },
                title: {
                    text: "",
                },
            },
            tooltip: {
                backgroundColor: "#ffffff",
                formatter: function () {
                    const color = this.y >= 0 ? "#008616" : "#4b4b4b";
                    return (
                        this.series.name +
                        ': <br/><span style="color: ' +
                        color +
                        '">£' +
                        HighStock.numberFormat(this.y, 2, ".", ",") +
                        "</span><br/>as at " +
                        HighStock.dateFormat("%d %b %Y", this.x)
                    );
                },
            },
        };
    }

    private GetGraphData(graphData: PerformanceGraphDataItemForDisplay[]) {
        const result = [];
        graphData.forEach((e) => {
            result.push([e.DateForChart, e.Value]);
        });
        return result;
    }

    private getCount = (type: SubAccountType) => {
        const preCount: number = this.settings?.[type] ? this.settings?.[type] : 0;
        const curCount = preCount + 1;
        this.settings[type] = curCount;
        return curCount;
    };

    private initialData(data: ProductGraphData[]) {
        const series = [];
        let count;
        data?.forEach((e) => {
            let color;
            switch (e.SubAccountType) {
                case SubAccountType.Pension:
                case SubAccountType.Sipp:
                case SubAccountType.Npr:
                    count = this.getCount(SubAccountType.Npr);
                    color = `var(--product-sipp-${count})`;
                    break;
                case SubAccountType.StocksIsa:
                    count = this.getCount(SubAccountType.StocksIsa);
                    color = `var(--product-isa-${count})`;
                    break;
                case SubAccountType.InvestmentAccount:
                    count = this.getCount(SubAccountType.InvestmentAccount);
                    color = `var(--product-pp-${count})`;
                    break;
                case SubAccountType.CashAccount:
                case SubAccountType.WrapCash:
                    count = this.getCount(SubAccountType.WrapCash);
                    color = `var(--product-wrap-cash-${count})`;
                    break;
                case SubAccountType.CashIsa:
                    count = this.getCount(SubAccountType.CashIsa);
                    color = `var(--product-cash-isa-${count})`;
                    break;
                case SubAccountType.OffshoreBond:
                    count = this.getCount(SubAccountType.OffshoreBond);
                    color = `var(--product-offshore-bond-${count})`;
                    break;
                case SubAccountType.OnshoreBond:
                    count = this.getCount(SubAccountType.OnshoreBond);
                    color = `var(--product-onshore-bond-${count})`;
                    break;
                case SubAccountType.NotSpecified:
                    color = "#000";
                    break;
                default:
                    color = "#4b4b4b";
            }
            series.push({
                type: "line",
                color: color,
                data: this.GetGraphData(e.GraphData),
                name: e.ClAccountId == "" ? "All" : e.ClAccountId,
                showInNavigator: true,
            });
        });
        return series;
    }

    private addEvent = new AdviserSiteTagManager().addEvent;

    public render(): JSX.Element {
        const { data, selectedProducts, onClickGraghSeriesBtn } = this.props;
        const { isPresentationView } = this.context;
        if (!data) {
            return <LoadingSkeleton type={LoadingSkeletonType.Table} />;
        }
        if (data.filter((x) => x.SubAccountType != SubAccountType.NotSpecified).length == 0) {
            return <Alert alertType={AlertType.Warning} message={getNoDataMsg("valuation and money movements")} />;
        }
        const stringHelper = new StringHelper();
        this.settings = {};
        return (
            <>
                <HighchartsReact
                    highcharts={HighStock}
                    options={this.getOptions(
                        this.initialData(
                            data?.filter((ele) => selectedProducts.some((product) => product == ele.ClAccountId)) || []
                        )
                    )}
                    allowChartUpdate={true}
                />

                <div className="row row-right">
                    <CoachMarker coachMarkerKey="performance-graph" />
                    {data.map((e) => {
                        return (
                            <>
                                <div className="col-auto pb-24">
                                    <div>
                                        <button
                                            aria-pressed={
                                                selectedProducts.some((x) => x == e.ClAccountId) ? "true" : "false"
                                            }
                                            className={"chart-legend-toggle-button "}
                                            onClick={() => {
                                                this.addEvent({
                                                    event: "performancetab_interaction",
                                                    eventParameters: {
                                                        eventCategory: eventCategoryForGTM.performanceTab,
                                                        eventAction: "chart filter",
                                                        eventLabel: `${subAccountTypeMap[e.SubAccountType]} ${
                                                            e.ClAccountId
                                                        } - ${
                                                            selectedProducts.some((x) => x == e.ClAccountId)
                                                                ? "no"
                                                                : "yes"
                                                        }`,
                                                    },
                                                });
                                                onClickGraghSeriesBtn(e.ClAccountId);
                                            }}
                                        >
                                            <span
                                                className={
                                                    "account-legend" + stringHelper.getDonutClass(e.SubAccountType)
                                                }
                                            ></span>
                                            <span className="account-legend-label">
                                                {subAccountTypeMap[e.SubAccountType] + " " + e.ClAccountId}
                                            </span>
                                            <span className="icon" aria-hidden={true}></span>
                                        </button>
                                    </div>
                                </div>
                            </>
                        );
                    })}
                </div>
            </>
        );
    }
}
PerformanceChart.contextType = PresentationContext;
